if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        console.log('ProductForm initialized', this);

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Empêcher la soumission accidentelle sur mobile via Enter
        this.initMobileKeyboardHandling();

        // Initialiser la validation des champs requis
        this.initRequiredFieldsValidation();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.startLoading();

        // Récupérer les informations du produit principal
        const formData = new FormData(this.form);
        const mainVariantId = formData.get('id');
        const mainProductId = this.dataset.productId || this.closest('[data-product-id]')?.dataset.productId;

        // Préparer la configuration pour l'ajout au panier
        const config = this.prepareCartConfig(formData);

        // Ajouter le produit principal au panier
        this.addMainProductToCart(config, mainVariantId, mainProductId).catch((error) => {
          console.error('Error adding main product to cart:', error);
          this.stopLoading();
        });
      }

      /**
       * Démarre l'animation de chargement
       */
      startLoading() {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
      }

      /**
       * Arrête l'animation de chargement
       */
      stopLoading() {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      /**
       * Prépare la configuration pour l'ajout au panier
       * @param {FormData} formData - Les données du formulaire
       * @returns {Object} La configuration pour la requête fetch
       */
      prepareCartConfig(formData) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;
        return config;
      }

      /**
       * Ajoute le produit principal au panier
       * @param {Object} config - La configuration pour la requête fetch
       * @param {string} mainVariantId - L'ID de la variante du produit principal
       * @param {string} mainProductId - L'ID du produit principal
       * @returns {Promise} Une promesse résolue avec la réponse de l'API
       */
      addMainProductToCart(config, mainVariantId, mainProductId) {
        return fetch(window.Shopify.routes.root + 'cart/add.js', config)
          .then((response) => response.json())
          .then((response) => {
            return this.handleMainProductResponse(response, config.body, mainVariantId, mainProductId);
          });
      }

      /**
       * Gère la réponse de l'ajout du produit principal
       * @param {Object} response - La réponse de l'API
       * @param {FormData} formData - Les données du formulaire
       * @param {string} mainVariantId - L'ID de la variante du produit principal
       * @param {string} mainProductId - L'ID du produit principal
       * @returns {Promise} Une promesse résolue avec la réponse de l'API
       */
      handleMainProductResponse(response, formData, mainVariantId, mainProductId) {
        // Cas d'erreur
        if (response.status) {
          this.handleProductError(response, formData);
          return Promise.reject(new Error('Product error'));
        }

        // Redirection si pas de panier
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return Promise.resolve(response);
        }

        // Vérifier s'il y a des upsells sélectionnés pour ce produit principal
        const hasUpsells =
          window.selectedUpsells &&
          window.selectedUpsells[mainProductId] &&
          window.selectedUpsells[mainProductId].size > 0;

        // Ajouter les upsells si nécessaire
        if (hasUpsells) {
          return this.addUpsellsToCart(mainProductId, mainVariantId, response);
        } else {
          this.updateCartWithoutUpsells(mainVariantId, response);
          return Promise.resolve(response);
        }
      }

      /**
       * Gère les erreurs lors de l'ajout du produit principal
       * @param {Object} response - La réponse d'erreur de l'API
       * @param {FormData} formData - Les données du formulaire
       */
      handleProductError(response, formData) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: response.errors || response.description,
          message: response.message,
        });
        this.handleErrorMessage(response.description);

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (!soldOutMessage) return;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        this.error = true;
        this.stopLoading();
      }

      /**
       * Ajoute les upsells au panier
       * @param {string} mainProductId - L'ID du produit principal
       * @param {string} mainVariantId - L'ID de la variante du produit principal
       * @param {Object} mainResponse - La réponse de l'API pour le produit principal
       * @returns {Promise} Une promesse résolue avec la réponse de l'API
       */
      addUpsellsToCart(mainProductId, mainVariantId, mainResponse) {
        const upsellItems = this.prepareUpsellItems(mainProductId);
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];

        // Ajouter les upsells au panier
        return fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: upsellItems,
            sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((upsellResponse) => upsellResponse.json())
          .then((upsellData) => {
            this.updateCartWithUpsells(mainVariantId, upsellData);
            return upsellData;
          })
          .catch((error) => {
            console.error('Error adding upsells to cart:', error);
            // En cas d'erreur avec les upsells, afficher quand même le panier avec le produit principal
            this.cart.renderContents(mainResponse);
            return mainResponse;
          })
          .finally(() => {
            this.stopLoading();
          });
      }

      /**
       * Prépare les éléments upsell pour l'ajout au panier
       * @param {string} mainProductId - L'ID du produit principal
       * @returns {Array} Un tableau d'objets représentant les upsells à ajouter
       */
      prepareUpsellItems(mainProductId) {
        const upsellItems = [];

        if (window.selectedUpsells[mainProductId]) {
          window.selectedUpsells[mainProductId].forEach((variantId) => {
            upsellItems.push({
              id: variantId,
              quantity: 1,
              properties: {
                _main_product_id: mainProductId,
                _add: true,
              },
            });
          });
        }

        return upsellItems;
      }

      /**
       * Met à jour le panier avec les upsells
       * @param {string} mainVariantId - L'ID de la variante du produit principal
       * @param {Object} cartData - Les données du panier
       */
      updateCartWithUpsells(mainVariantId, cartData) {
        // Mettre à jour le panier après l'ajout des upsells
        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: mainVariantId,
            cartData: cartData,
          });
        }

        this.error = false;
        this.updateCartUI(cartData);
      }

      /**
       * Met à jour le panier sans upsells
       * @param {string} mainVariantId - L'ID de la variante du produit principal
       * @param {Object} cartData - Les données du panier
       */
      updateCartWithoutUpsells(mainVariantId, cartData) {
        // Pas d'upsells, mettre à jour le panier directement
        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: mainVariantId,
            cartData: cartData,
          });
        }

        this.error = false;
        this.updateCartUI(cartData);
        this.stopLoading();
      }

      /**
       * Met à jour l'interface utilisateur du panier
       * @param {Object} cartData - Les données du panier
       */
      updateCartUI(cartData) {
        const quickAddModal = this.closest('quick-add-modal');
        const detailsModal = this.closest('.card__details-modal');

        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                this.cart.renderContents(cartData);
              });
            },
            { once: true },
          );
          quickAddModal.hide(true);
        } else if (detailsModal) {
          detailsModal.click();
          this.cart.renderContents(cartData);
        } else {
          this.cart.renderContents(cartData);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      initRequiredFieldsValidation() {
        if (!this.closest('product-info')) {
          return;
        }
        // Récupérer tous les champs requis dans le formulaire
        this.requiredFields = this.closest('product-info').querySelectorAll('[required]');

        if (this.requiredFields.length === 0) return;

        // Ajouter des écouteurs d'événements pour chaque champ requis
        this.requiredFields.forEach((field) => {
          field.addEventListener('input', this.validateRequiredFields.bind(this));
          field.addEventListener('change', this.validateRequiredFields.bind(this));
        });

        // Valider les champs au chargement
        if (this.submitButton && this.submitButton.classList.contains('product-form__submit')) {
          this.validateRequiredFields();
        }
      }

      validateRequiredFields() {
        if (this.requiredFields.length === 0) return;

        let allValid = true;

        // Vérifier chaque champ requis
        this.requiredFields.forEach((field) => {
          let isValid = false;

          // Vérifier différents types de champs
          if (field.type === 'checkbox') {
            isValid = field.checked;
          } else if (field.type === 'file') {
            isValid = field.files && field.files.length > 0;
          } else {
            isValid = field.value.trim() !== '';
          }

          // Si un champ n'est pas valide, tous les champs ne sont pas valides
          if (!isValid) {
            allValid = false;
          }
        });

        // Mettre à jour l'état du bouton d'ajout au panier
        if (this.submitButton) {
          if (allValid) {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButtonText.innerHTML =
              '<span class="product-form__submit-long">' +
              window.variantStrings.addToCart +
              '</span><span class="product-form__submit-short">' +
              window.variantStrings.add +
              '</span>';
            this.submitButtonText.classList.remove('product-form__submit-incomplete');
          } else {
            this.submitButton.setAttribute('disabled', '');
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.submitButtonText.textContent = window.variantStrings.inputRequired || 'Champs requis';
            this.submitButtonText.classList.add('product-form__submit-incomplete');
          }
        }
      }

      /**
       * Initialise la gestion du clavier mobile pour empêcher la soumission accidentelle
       */
      initMobileKeyboardHandling() {
        // Récupérer tous les champs de saisie dans le formulaire et dans le produit-info parent
        const productInfo = this.closest('product-info');

        if (!productInfo) return;

        const inputFields = productInfo.querySelectorAll(
          'input:not([type="submit"]):not([type="button"]), textarea, select',
        );

        // Ajouter l'écouteur sur chaque champ
        inputFields.forEach((field) => {
          field.addEventListener('keydown', this.handleFieldKeyDown.bind(this));
        });
      }

      /**
       * Gère l'événement keydown sur un champ de saisie spécifique
       * @param {KeyboardEvent} evt - L'événement clavier
       */
      handleFieldKeyDown(evt) {
        // Si c'est la touche Enter et qu'on est sur mobile
        if (evt.key === 'Enter' && this.isMobileDevice()) {
          evt.preventDefault();
          evt.stopPropagation();
          evt.target.blur(); // Ferme le clavier mobile
          return false;
        }
      }

      /**
       * Détecte si l'utilisateur est sur un appareil mobile
       * @returns {boolean} True si mobile, false sinon
       */
      isMobileDevice() {
        return (
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
          window.innerWidth <= 768
        );
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
